// src/utils/indirizzi.js
// ═══════════════════════════════════════════════════════════════════════════
// INDIRIZZI COMPLETI E CONTROLLATI (W438) — logica pura, provata a parte
// ═══════════════════════════════════════════════════════════════════════════
// Un indirizzo scritto tutto in una riga («via roma napoli») porta il navigatore e la mappa
// dalla parte sbagliata: squadre e consegne in un'altra città costano. Per questo l'indirizzo
// si scrive a pezzi — via, numero civico, CAP, comune, provincia — e Nexiora ne compone la
// riga completa («Via Roma 12, 81020 San Marco Evangelista (CE)») usata da mappe, PDF e
// documenti. Il comune si sceglie dall'elenco ISTAT (provincia e CAP si compilano da soli)
// e se CAP e comune non corrispondono lo dice, senza bloccare: l'elenco può non conoscere
// un comune nuovo o un CAP cambiato.
// ═══════════════════════════════════════════════════════════════════════════

export const PARTI_VUOTE = { via: "", civico: "", cap: "", comune: "", provincia: "" };
const pulisci = (t) => String(t ?? "").replace(/\s+/g, " ").trim();
const norm = (t) => pulisci(t).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]/g, "");

/** «Via Roma 12, 81020 San Marco Evangelista (CE)» (vuoto se mancano via e comune). */
export function componiIndirizzo(p = {}) {
  const via = [pulisci(p.via), pulisci(p.civico)].filter(Boolean).join(" ");
  const sigla = pulisci(p.provincia).toUpperCase();
  const luogo = [pulisci(p.cap), pulisci(p.comune)].filter(Boolean).join(" ") + (sigla ? ` (${sigla})` : "");
  return [via, luogo.trim()].filter(Boolean).join(", ");
}

/** Errori bloccanti (campi mancanti o scritti male), uno per campo: { via, civico, cap, comune, provincia }. */
export function erroriIndirizzo(p = {}) {
  const e = {};
  if (!pulisci(p.via)) e.via = "Scrivi via, piazza o strada";
  if (!pulisci(p.civico)) e.civico = "Scrivi il numero civico (o «snc» se non c'è)";
  if (!/^\d{5}$/.test(pulisci(p.cap))) e.cap = "Il CAP ha 5 cifre";
  if (!pulisci(p.comune)) e.comune = "Scrivi il comune";
  if (!/^[A-Za-z]{2}$/.test(pulisci(p.provincia))) e.provincia = "Sigla di 2 lettere (es. NA)";
  return e;
}
export const indirizzoCompletoOk = (p) => Object.keys(erroriIndirizzo(p)).length === 0;

/**
 * Prova a dividere un indirizzo scritto in una riga, per precompilare i campi quando si apre
 * un cliente salvato prima: «Via Roma 12, 81020 San Marco Evangelista (CE)». Quello che non
 * riconosce lo lascia nella via, da sistemare a mano.
 */
export function scomponiIndirizzo(testo) {
  const t = pulisci(testo);
  const out = { ...PARTI_VUOTE };
  if (!t) return out;
  let resto = t;
  const prov = resto.match(/\(([A-Za-z]{2})\)\s*$/) || resto.match(/[\s,-]+([A-Z]{2})\s*$/);
  if (prov) { out.provincia = prov[1].toUpperCase(); resto = resto.slice(0, prov.index).trim().replace(/[,\s-]+$/, ""); }
  const cap = resto.match(/(?:^|[\s,-])(\d{5})(?:\s+|,\s*)([^,\d][^,]*)$/);
  if (cap) { out.cap = cap[1]; out.comune = pulisci(cap[2]); resto = resto.slice(0, cap.index).trim().replace(/[,\s-]+$/, ""); }
  else {
    const virg = resto.lastIndexOf(",");
    if (virg > 0 && !/\d/.test(resto.slice(virg + 1))) { out.comune = pulisci(resto.slice(virg + 1)); resto = resto.slice(0, virg).trim(); }
  }
  const civ = resto.match(/^(.*?)[\s,]+(\d+[A-Za-z]?(?:\/[A-Za-z0-9]+)?|snc|s\.n\.c\.|km\s*[\d.,]+)$/i);
  if (civ) { out.via = pulisci(civ[1]).replace(/,$/, ""); out.civico = pulisci(civ[2]); }
  else out.via = resto;
  return out;
}

/** Elenco dei comuni dai dati compatti («Nome|SIGLA|cap1,cap2»). */
export function leggiComuni(dati) {
  return String(dati || "").split("\n").filter(Boolean).map(r => { const [nome, sigla, cap] = r.split("|"); return { nome, sigla, cap: (cap || "").split(",").filter(Boolean) }; });
}
/** Il comune con quel nome (e, se data, quella provincia). */
export function trovaComune(comuni, nome, sigla = "") {
  const n = norm(nome);
  if (!n) return null;
  const tutti = comuni.filter(c => norm(c.nome) === n);
  if (!tutti.length) return null;
  return (sigla && tutti.find(c => c.sigla === String(sigla).toUpperCase())) || tutti[0];
}
/** Comuni che iniziano come il testo scritto (per i suggerimenti), i più brevi prima. */
export function suggerisciComuni(comuni, testo, max = 12) {
  const n = norm(testo);
  if (n.length < 2) return [];
  const inizio = comuni.filter(c => norm(c.nome).startsWith(n));
  const dentro = inizio.length < max ? comuni.filter(c => !norm(c.nome).startsWith(n) && norm(c.nome).includes(n)) : [];
  return [...inizio.sort((a, b) => a.nome.length - b.nome.length), ...dentro].slice(0, max);
}
/** Comuni che hanno quel CAP. */
export const comuniDelCap = (comuni, cap) => (/^\d{5}$/.test(pulisci(cap)) ? comuni.filter(c => c.cap.includes(pulisci(cap))) : []);

/** «80121–80147» oppure «81020»: i CAP di un comune in breve. */
export function capInBreve(caps = []) {
  if (!caps.length) return "";
  if (caps.length === 1) return caps[0];
  const s = [...caps].sort();
  return `${s[0]}–${s[s.length - 1]}`;
}

/**
 * Avvisi (non bloccanti) sulla coerenza con l'elenco dei comuni: comune sconosciuto, provincia
 * diversa, CAP che non è di quel comune. [] se tutto torna o se l'elenco non è ancora caricato.
 */
export function avvisiIndirizzo(comuni, p = {}) {
  if (!Array.isArray(comuni) || !comuni.length || !pulisci(p.comune)) return [];
  const a = [];
  const c = trovaComune(comuni, p.comune, p.provincia);
  if (!c) {
    a.push(`«${pulisci(p.comune)}» non è nell'elenco dei comuni: controlla come è scritto.`);
    return a;
  }
  const sigla = pulisci(p.provincia).toUpperCase();
  if (sigla && /^[A-Z]{2}$/.test(sigla) && c.sigla !== sigla) a.push(`${c.nome} è in provincia di ${c.sigla}, non ${sigla}.`);
  const cap = pulisci(p.cap);
  if (/^\d{5}$/.test(cap) && c.cap.length && !c.cap.includes(cap)) {
    const altri = comuniDelCap(comuni, cap).map(x => `${x.nome} (${x.sigla})`).slice(0, 3);
    a.push(`Il CAP ${cap} non risulta di ${c.nome} (CAP: ${capInBreve(c.cap)})${altri.length ? `: è di ${altri.join(", ")}` : ""}. Un CAP sbagliato porta navigatore e consegne da un'altra parte.`);
  }
  return a;
}

/** Link per controllare l'indirizzo sulla mappa prima di salvarlo. */
export function linkVerificaMappa(p) {
  const t = componiIndirizzo(p);
  return t ? "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(t + ", Italia") : null;
}
